/**
 * Approver queue — V98-3-4 (PRD G-2 "approver: things to sign or provide";
 * research §2 items 21, 23). Order: overdue first, then what blocks a
 * filing, then due (lib/today-model approverOrder).
 *
 * Each signature is a SignatureCard (C22): title + LOCKED version + time
 * with "Read vN" (the locked content itself), "You certify …", the
 * consequence of not signing, what waits on it. Actions: sign (passkey, or
 * a 6-digit code fallback — the server records the method; the passkey
 * assertion itself is not verified server-side yet, so the UI does not
 * claim it is), send back with a reason. A delegate signs "for X under
 * delegation <id>".
 *
 * V98-3 review:
 *   U6  ONE filled primary per visible layer — only the HEAD of the queue
 *       signs as primary; every other card's sign button is secondary.
 *   U9  send-back errors render inside its dialog; errors clear on change.
 *   U10 "locked", Read vN, and Signed today in the viewer's own time zone
 *       with "Undo · N h left" while the undo window is open.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, SignatureCard, Textarea } from '@aigovops/design-system';
import type { OwedRow } from '@aigovops/core';
import { getSignature, listMySignatures, sendBackSignature, signSignature, undoSignature, type SignatureView } from '../../lib/coverage-api';
import { invalidationMap } from '../../lib/query-keys';
import { actionName, dueChip, firstName, fmtDay, itemHref, nameOf, rowAct, titleWithoutVersion, type Names } from '../../lib/today-model';
import { useClock } from '../../lib/clock';
import { ActionDialog, Chip, domId, errorText } from './shared';

export const CERTIFY: Record<string, string> = {
  policy: 'This policy is approved and binds everyone in scope from its effective date.',
  risk_acceptance: 'I accept this risk, as described, until the date in the acceptance.',
  certification_package: 'This package is accurate and complete as locked, and I authorize its release.',
  system_description: 'This system description fairly presents the system as locked.',
  scn: 'This change is classified correctly and the notice is accurate.',
  access_review: 'These rows were reviewed and the decisions are appropriate.',
  management_response: 'This management response is accurate and approved.',
};

/** A timestamp in the VIEWER's time zone (year, time and zone shown — R5 records). */
export const fmtStamp = (iso: string | null | undefined, timeZone?: string) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short', ...(timeZone ? { timeZone } : {}) }) : '';

/** The viewer's calendar day for an instant (their own time zone, not UTC — U10). */
export function localDay(iso: string, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...(timeZone ? { timeZone } : {}) }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** "Undo · 3 h left" / "Undo · 20 min left" while the undo window is open. */
export function undoLeft(undoUntil: string | null, now = Date.now()): string | null {
  if (!undoUntil) return null;
  const ms = Date.parse(undoUntil) - now;
  if (!(ms > 0)) return null;
  const h = Math.floor(ms / 3_600_000);
  return h >= 1 ? `${h} h left` : `${Math.max(1, Math.round(ms / 60_000))} min left`;
}

const CODE_RX = /^\d{6}$/;

async function refreshAfterSign(qc: ReturnType<typeof useQueryClient>, slug: string) {
  await Promise.all([
    ...invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })),
    qc.invalidateQueries({ queryKey: ['signatures', slug] }),
    qc.invalidateQueries({ queryKey: ['blockers', slug] }),
  ]);
}

/** One signable request as a SignatureCard with sign / send back. Also used by the item page. */
export function SignatureAction({ slug, request, row, names, todayDay, onDone, programNames, primary = true, readTo, willMiss = false }: {
  slug: string; request: SignatureView; row: Pick<OwedRow, 'key' | 'title' | 'hard_date' | 'due_at' | 'on_behalf_of_user_id' | 'delegation_id' | 'program_ids'> | null;
  names: Names; todayDay: string; onDone: (msg: string, focusId: string) => void; programNames?: ReadonlyMap<string, string>;
  /** The filled primary of its layer (the queue head, or the item page's own card) — U6. */
  primary?: boolean;
  /** Where "Read vN" opens the locked content. */
  readTo?: string;
  /** R3-U9: the item is projected to miss its hard date — the consequence reads red, like the band. */
  willMiss?: boolean;
}) {
  const qc = useQueryClient();
  const [useCode, setUseCode] = useState(false);
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendBack, setSendBack] = useState(false);
  const [sendBackErr, setSendBackErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonErr, setReasonErr] = useState<string | null>(null);
  const id = request.request_id;
  const behalf = row?.on_behalf_of_user_id ?? null;
  const delegation = row?.delegation_id ?? null;

  const hard = request.hard_date ?? row?.hard_date ?? null;
  const due = request.due_at ?? row?.due_at ?? null;
  const consequence = hard
    ? { label: `If unsigned by ${fmtDay(hard)}: blocks a filing`, tone: (willMiss || dueChip({ due_at: null, hard_date: hard }, todayDay).tone === 'red' ? 'danger' : 'warning') as 'danger' | 'warning' }
    : due ? { label: `Due ${fmtDay(due)}`, tone: 'neutral' as const } : undefined;
  const content = (request.content ?? {}) as { blocks?: string[] };
  const deps = [
    ...(content.blocks ?? []).map((label) => ({ label })),
    ...(row?.program_ids ?? []).map((p) => programNames?.get(p)).filter(Boolean).map((label) => ({ label: label as string })),
  ];

  const sign = async () => {
    if (busy) return;
    if (useCode && !CODE_RX.test(code)) { setCodeErr('Enter the 6 digits from your authenticator app.'); return; }
    setCodeErr(null); setBusy(true); setError(null);
    try {
      await signSignature(slug, id, {
        version: request.version, content_sha256: request.content_sha256, method: useCode ? 'code' : 'passkey',
        ...(useCode ? { assertion: code } : {}),
        ...(behalf && delegation ? { on_behalf_of_user_id: behalf, delegation_id: delegation } : {}),
      });
      await refreshAfterSign(qc, slug);
      onDone(`Signed ${request.title}${behalf ? ` for ${firstName(nameOf(names, behalf))}` : ''}`, domId('signed', id));
    } catch (e) { setError(errorText(e, 'The signature could not be recorded.')); }
    finally { setBusy(false); }
  };

  const submitSendBack = async () => {
    const t = reason.trim();
    if (!t) { setReasonErr('Say what needs to change (required).'); return false; }
    setReasonErr(null); setBusy(true); setSendBackErr(null);
    try {
      await sendBackSignature(slug, id, t);
      setSendBack(false);
      await refreshAfterSign(qc, slug);
      onDone(`Sent back ${request.title}`, 'owed-h');
    } catch (e) { setSendBackErr(errorText(e, 'The send-back could not be recorded.')); return false; }
    finally { setBusy(false); }
    return true;
  };

  const state = request.state === 'obsolete' ? 'obsolete' : request.state === 'signed' ? 'signed' : 'open';
  const signLabel = useCode ? 'Sign with code' : 'Sign with passkey';
  // R3-U25: the card shows the version next to the title — never "· v5 v5".
  const shownTitle = titleWithoutVersion(request.title, request.version);
  return (
    <>
      <SignatureCard
        title={shownTitle}
        version={`v${request.version}`}
        frozenAt={fmtStamp(request.frozen_at)}
        frozenAtDateTime={request.frozen_at}
        readLink={readTo ? <Link to={readTo} aria-label={`Read v${request.version} · ${shownTitle}`}>{`Read v${request.version}`}</Link> : undefined}
        certify={CERTIFY[request.artifact_kind] ?? 'This item is accurate as locked and approved.'}
        consequence={consequence}
        dependencies={deps}
        state={state}
        stateReason={state === 'obsolete' ? (request.reason ?? 'A prerequisite changed; a new version will be locked.') : undefined}
        signedBy={state === 'signed' ? `${nameOf(names, request.signed_by_user_id)} · ${fmtStamp(request.signed_at)}` : undefined}
        sendBack={<Button id={domId('sendback', id)} onClick={() => setSendBack(true)} aria-label={`Send back · ${shownTitle}`}>Send back…</Button>}
      >
        <span className="gp-actions">
          {behalf ? <Chip text={`For ${firstName(nameOf(names, behalf))} · delegation`} tone="outline" /> : null}
          {useCode ? (
            <Field id={domId('code', id)} label="6-digit code" hint="From your authenticator app" error={codeErr ?? undefined}>
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => { setCode(e.currentTarget.value.replace(/\D/g, '')); setCodeErr(null); setError(null); }} />
            </Field>
          ) : null}
          {/* R3-U25: names carry the item so a screen-reader list of buttons tells the cards apart. */}
          <Button variant="ghost" aria-label={`${useCode ? 'Use passkey' : 'Use a code instead'} · ${shownTitle}`} onClick={() => { setUseCode((u) => !u); setCodeErr(null); setError(null); }}>{useCode ? 'Use passkey' : 'Use a code instead'}</Button>
          <Button variant={primary ? 'primary' : 'secondary'} aria-disabled={busy || undefined} onClick={() => void sign()} aria-label={`${signLabel} · ${shownTitle}`}>
            {busy ? 'Signing…' : signLabel}
          </Button>
        </span>
      </SignatureCard>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <ActionDialog open={sendBack} title={`Send back · ${shownTitle}`} submitLabel="Send back" busy={busy} error={sendBackErr}
        onClose={() => { setSendBack(false); setReasonErr(null); setSendBackErr(null); }} onSubmit={submitSendBack}>
        <Field id={domId('reason', id)} label="What needs to change" required hint="Required · up to 2000 characters · goes to the author" error={reasonErr ?? undefined}>
          <Textarea rows={4} maxLength={2000} value={reason} onChange={(e) => { setReason(e.currentTarget.value); setReasonErr(null); setSendBackErr(null); }} />
        </Field>
      </ActionDialog>
    </>
  );
}

export function SignQueue({ rows, slug, prefix, names, todayDay, programNames, missKeys, onDone }: {
  rows: readonly OwedRow[]; slug: string; prefix: string; names: Names; todayDay: string; programNames: ReadonlyMap<string, string>;
  /** R3-U8: items projected to miss their hard date — approverOrder puts them first, so the head IS the miss. */
  missKeys?: ReadonlySet<string>;
  onDone: (msg: string, focusId: string) => void;
}) {
  return (
    <ol className="gp-sign-list" aria-label="To sign">
      {rows.map((r, i) => <SignRow key={r.key} row={r} head={i === 0} willMiss={!!missKeys?.has(r.key)} slug={slug} prefix={prefix} names={names} todayDay={todayDay} programNames={programNames} onDone={onDone} />)}
    </ol>
  );
}

function SignRow({ row, head, willMiss, slug, prefix, names, todayDay, programNames, onDone }: {
  row: OwedRow; head: boolean; willMiss: boolean; slug: string; prefix: string; names: Names; todayDay: string; programNames: ReadonlyMap<string, string>; onDone: (msg: string, focusId: string) => void;
}) {
  const isSig = row.ref.kind === 'signature_request';
  const sig = useQuery({ queryKey: ['signature', slug, row.ref.id], queryFn: () => getSignature(slug, row.ref.id), enabled: isSig, staleTime: 30_000, retry: false });
  const chip = dueChip(row, todayDay);
  if (!isSig || sig.error || !sig.data) {
    const act = rowAct(row, prefix);
    const to = act.kind === 'link' ? act.to : itemHref(prefix, row.ref);
    return (
      <li className="gp-ow" id={domId('ow', row.key)} tabIndex={-1}>
        <span className="gp-ow-main">
          <span className="gp-ow-line"><Link className="gp-ow-t" to={itemHref(prefix, row.ref)}>{row.title}</Link></span>
          <span className="gp-ow-meta"><Chip text={willMiss && row.hard_date ? `Will miss ${fmtDay(row.hard_date)}` : chip.text} tone={willMiss ? 'red' : chip.tone} />{row.quantity > 1 ? <span>{`${row.quantity} rows`}</span> : null}</span>
        </span>
        <span className="gp-ow-act"><Link className={`gp-linkbtn gp-linkbtn--compact${head ? ' btn-primary' : ''}`} to={to} aria-label={actionName(act.label, row.title)}>{act.label}</Link></span>
      </li>
    );
  }
  return (
    <li id={domId('ow', row.key)} tabIndex={-1}>
      <SignatureAction slug={slug} request={sig.data.request} row={row} names={names} todayDay={todayDay} programNames={programNames} onDone={onDone}
        primary={head} willMiss={willMiss} readTo={`${itemHref(prefix, row.ref)}#locked`} />
    </li>
  );
}

/** Signed today (yours, in your own time zone), with undo while the undo window is open. */
export function SignedToday({ slug, viewerId, names, onDone, timeZone }: {
  slug: string; viewerId: string; names: Names; onDone: (msg: string, focusId: string) => void;
  /** Tests pin a zone; the app uses the viewer's own. */
  timeZone?: string;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['signatures', slug, 'signed'], queryFn: () => listMySignatures(slug, 'signed'), staleTime: 30_000, retry: false });
  const [err, setErr] = useState<string | null>(null);
  // R3-S10: the clock and zone come from ClockContext (pinned in tests), never the bare wall clock.
  const clock = useClock();
  const now = clock.now();
  const zone = timeZone ?? clock.timeZone;
  const today = localDay(new Date(now).toISOString(), zone);
  const rows = (q.data?.requests ?? []).filter((r) => r.signed_by_user_id === viewerId && r.signed_at && localDay(r.signed_at, zone) === today);
  if (!rows.length) return null;
  const undo = async (r: SignatureView) => {
    setErr(null);
    try {
      await undoSignature(slug, r.request_id);
      await refreshAfterSign(qc, slug);
      onDone(`Undone · ${r.title} is back in To sign`, 'owed-h');
    } catch (e) { setErr(errorText(e, 'Undo is no longer possible.')); }
  };
  return (
    <section className="gp-sec" aria-labelledby="signed-h">
      <div className="gp-sec-head"><h2 id="signed-h" className="gp-sec-h">Signed today</h2></div>
      {err ? <Alert variant="error">{err}</Alert> : null}
      <ul className="gp-owed-list" aria-label="Signed today">
        {rows.map((r) => {
          // Undo runs until the downstream send, and until undo_until when the server sets one.
          const left = r.downstream_sent_at ? null : r.undo_until ? undoLeft(r.undo_until, now) : 'until it’s sent';
          return (
            <li key={r.request_id} className="gp-ow" id={domId('signed', r.request_id)} tabIndex={-1}>
              <span className="gp-ow-main">
                <span className="gp-ow-line"><span className="gp-ow-t">{`${titleWithoutVersion(r.title, r.version)} · v${r.version}`}</span></span>
                <span className="gp-ow-meta"><Chip text={`Signed ${fmtStamp(r.signed_at, zone)}`} tone="ok" />{r.on_behalf_of_user_id ? <span>{`for ${nameOf(names, r.on_behalf_of_user_id)}`}</span> : null}</span>
              </span>
              <span className="gp-ow-act">
                {left ? <Button compact aria-label={`Undo · ${left} · ${r.title}`} onClick={() => void undo(r)}>{`Undo · ${left}`}</Button> : <Chip text="Sent · undo closed" tone="lock" />}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
